/**
 * Central permission definitions.
 *
 * Every role check in the app resolves through `can()` / `usePermissions()` —
 * no ad-hoc `role === 'admin'` comparisons in components.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SECURITY: enforced server-side too, not just here. `roles_router.py`'s
 * `require_permission` checks the same catalogue against the live
 * `role_permissions` table on every request; `home:edit`/`users:manage`/
 * `roles:manage` route gates and `GET /modules`'s own scoping are the
 * server-side boundary. This file — and `can()` in particular — is
 * defence-in-depth for the UI, not the boundary itself.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * WHY `can()` NO LONGER TAKES A STATIC LOOKUP
 * Roles used to be a fixed two-value set, so "what can this role do" was a
 * compile-time constant (`ROLE_PERMISSIONS[role]`). Now that an admin can
 * create custom roles with their own hand-picked permission sets (Settings ›
 * Roles & Permissions), that answer only exists as data — fetched once via
 * `roleService.hydrate()` (alongside `moduleService.hydrate()`, see
 * AuthContext) and read back synchronously here, the same "hydrate once, read
 * from memory" shape `moduleService`'s own cache already uses.
 */

import { roleService } from '../services/roleService';

export type Permission =
  | 'home:edit'
  | 'users:manage'
  | 'roles:manage'
  | 'modules:view-all'
  | 'modules:create'
  | 'modules:share'
  | 'account:edit'
  // Feature DATA editing (the Edit tool) — a strict superset relationship,
  // the same one Esri's own hosted feature layer editing has between
  // "Update" and its "Attributes only" sub-option: `data:edit` covers
  // everything `data:edit-attributes` does, plus geometry create/move/
  // delete. See MapViewOL/EditPanels.tsx for where these actually gate.
  | 'data:edit'
  | 'data:edit-attributes'
  // Create a group, rename/delete it, manage its members, and grant it
  // applications — NOT the same as sharing one of your own layers with an
  // existing group, which stays the layer owner's own call (LayerShareDialog),
  // no permission needed. See Settings › User Groups.
  | 'groups:manage';

/**
 * Whether the SIGNED-IN user's own role grants `permission`.
 *
 * `role` is accepted for signature compatibility with existing call sites
 * (`can(permission, currentUser?.role)`) but is otherwise unused — the only
 * permission set available client-side is the caller's own
 * (`GET /roles/mine`; the full roster needs `roles:manage` most users don't
 * have), so there is nothing else `can()` could answer even if a different
 * role id were passed. Before `roleService.hydrate()` resolves, this fails
 * closed (empty permission list), matching the old code's `!role` → `false`.
 */
export function can(permission: Permission, role: string | undefined): boolean {
  if (!role) return false;
  return roleService.myPermissions().includes(permission);
}

export const PERMISSION_LABELS: Record<Permission, string> = {
  'home:edit': 'Edit home page content',
  'users:manage': 'Create, edit, and disable users',
  'roles:manage': 'View and manage roles',
  'modules:view-all': 'View all modules in the portal',
  'modules:create': 'Create new modules',
  'modules:share': 'Share modules with others',
  'account:edit': 'Edit own account settings',
  'data:edit': 'Edit feature geometry & attributes',
  'data:edit-attributes': 'Edit feature attributes only',
  'groups:manage': 'Create and manage user groups',
};
